import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import arrangementsAdapter from './saved_arrangements.adapter';
import actions from './saved_arrangements.actions';

import canvases from 'domains/canvases';

import type {
  SavedArrangementsSliceInitialState,
  UnavailableArrangement,
} from './saved_arrangements.types';

const initialState: SavedArrangementsSliceInitialState = {
  status: 'idle',
  active: null,
  unavailable: null,
  unavailableIds: [],
};

const savedArrangements = createSlice({
  name: 'savedArrangements',
  initialState: arrangementsAdapter.getInitialState(initialState),
  reducers: {
    updateOne: arrangementsAdapter.updateOne,
    addUnavailableArrangements(state, { payload }: PayloadAction<UnavailableArrangement>) {
      return {
        ...state,
        unavailable: payload,
        unavailableIds: [...state.unavailableIds, payload.id],
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(actions.create.pending, (state) => {
        return {
          ...state,
          status: 'loading',
        };
      })
      .addCase(actions.create.fulfilled, (state, { payload }) => {
        const arrangement = payload;
        const update = arrangementsAdapter.addOne(state, {
          ...arrangement,
          canvases: arrangement.canvases,
          crops: arrangement.crops,
        });

        update.active = payload.id;
        update.status = 'idle';

        return update;
      })
      .addCase(actions.update.pending, (state) => {
        return {
          ...state,
          status: 'loading',
        };
      })
      .addCase(actions.update.fulfilled, (state, { payload }) => {
        if (!payload) return state;

        const update = arrangementsAdapter.updateOne(state, {
          id: payload.id,
          changes: {
            ...payload,
            canvases: payload.canvases,
            crops: payload.crops,
          },
        });

        update.active = payload.id;
        update.status = 'idle';

        return update;
      })
      .addCase(actions.destroy.pending, (state) => {
        return {
          ...state,
          status: 'loading',
        };
      })
      .addCase(actions.destroy.fulfilled, (state, { payload }) => {
        const update = arrangementsAdapter.removeOne(state, payload);

        if (update.active === payload) {
          update.active = null;
        }

        if (update.unavailable && update.unavailable.id === payload) {
          update.unavailable = null;
        }

        update.status = 'idle';

        return update;
      })
      .addCase(actions.getAll.pending, (state) => {
        return {
          ...state,
          status: 'loading',
        };
      })
      .addCase(actions.getAll.fulfilled, (state, { payload }) => {
        const arrangements = payload.map((arrangement) => {
          return {
            ...arrangement,
            canvases: arrangement.canvases,
            crops: arrangement.crops,
          };
        });

        const update = arrangementsAdapter.upsertMany(state, arrangements);
        update.status = 'idle';
      })
      .addCase(canvases.actions.restoreArrangement, (state, { payload }) => {
        return {
          ...state,
          active: payload.id,
          unavailable: null,
        };
      });
  },
});

export default savedArrangements;
